import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { UserService } from '../../core/services/user.service';
import { StudioService } from '../../core/services/studio.service';
import { OwnerService } from '../../core/services/owner.service';
import { KeyNumberService } from '../../core/services/key-number.service';
import { AuthStateService } from '../../core/services/auth-state.service';
import { FlashService } from '../../core/services/flash.service';

@Component({
    selector: 'app-staff-edit',
    imports: [RouterLink, FormsModule],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-lg-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-edit"></i>&nbsp;編集</span>
        </div>
        <div class="card-body">
          <div style="height: 1px"></div>
          @if (loading) {
            <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
          }
          @if (!loading) {
            <form novalidate name="staffEditForm" #staffEditForm="ngForm" (ngSubmit)="save(staffEditForm)">
              <div class="row">
                <div class="col-md-12">

                  <!-- 所属（表示のみ） -->
                  <div class="form-group row">
                    <div class="col-md-4">
                      <label class="col-form-label">所属&nbsp;:&nbsp;{{ ownerName }}</label>
                    </div>
                  </div>

                  <!-- 店舗（multi-select） -->
                  <div class="row">
                    <div class="col-md-4">
                      <label class="col-form-label">店舗</label>
                    </div>
                  </div>
                  <div class="form-group col-md-6 border" style="max-height:300px;overflow-y:auto">
                    <table class="table table-sm mt-2 mb-2">
                      @for (studio of filteredStudios; track studio.id) {
                        <tr>
                          <td class="border-0">
                            <div class="custom-control custom-checkbox custom-control-inline w-100">
                              <input class="custom-control-input"
                                     [id]="'check_' + studio.id"
                                     type="checkbox"
                                     [(ngModel)]="studioCheck[studio.id]"
                                     [name]="'studio_' + studio.id" [disabled]="userPermission > 10">
                              <label class="custom-control-label w-100" [for]="'check_' + studio.id">
                                {{ studio.name }}
                              </label>
                            </div>
                          </td>
                        </tr>
                      }
                    </table>
                  </div>

                  <!-- 従業員名 -->
                  <div class="form-group row">
                    <div class="col-md-8">
                      <label class="col-form-label">従業員名</label>
                      <input type="text" class="form-control" [(ngModel)]="form.name" name="name" required>
                    </div>
                  </div>

                  <!-- パスワード -->
                  <div class="form-group row">
                    <div class="col-md-8">
                      <label class="col-form-label">パスワード（変更する際は入力してください）</label>
                      <div class="input-group">
                        <input [type]="showPassword ? 'text' : 'password'" class="form-control" [(ngModel)]="form.password" name="password"
                               autocomplete="new-password" [pattern]="passwordPattern"
                               placeholder="半角英字、数字、記号で入力してください">
                        <div class="input-group-append">
                          <button class="btn btn-outline-secondary" type="button" (click)="showPassword = !showPassword">
                            <span [class]="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- メールアドレス -->
                  <div class="form-group row">
                    <div class="col-md-8">
                      <label class="col-form-label">メールアドレス</label>
                      <input type="email" class="form-control" [(ngModel)]="form.email" name="email" required email>
                    </div>
                  </div>

                  <!-- 入退館キー -->
                  <div class="form-group row">
                    <div class="col-md-4">
                      <label class="col-form-label">入退館キー</label>
                      <select class="form-control" [(ngModel)]="selectedKey" name="keyNumber">
                        <option [ngValue]="null">選択してください</option>
                        @for (item of filteredKeyItems; track item.id) {
                          <option [ngValue]="item">{{ item.keyNumber }}</option>
                        }
                      </select>
                    </div>
                  </div>

                  <!-- ボタン -->
                  <div class="form-group row">
                    <div class="col-md-12 text-right">
                      <a routerLink="/staff" class="btn bg-close text-white">
                        <i class="fa s-icon-cancel"></i>&nbsp;閉じる
                      </a>
                      <button type="submit" class="btn btn-success ml-1"
                              [disabled]="saving || staffEditForm.invalid || !hasCheckedStudio()">
                        <i class="fa s-icon-save"></i>&nbsp;保存
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            </form>
          }
        </div>
      </div>
    </div>
  </div>
</div>
  `
})
export class StaffEditComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private userService = inject(UserService);
  private studioService = inject(StudioService);
  private ownerService = inject(OwnerService);
  private keyNumberService = inject(KeyNumberService);
  private authState = inject(AuthStateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private flash = inject(FlashService);

  form: any = {};
  showPassword = false;
  ownerName = '';
  filteredStudios: any[] = [];
  studioCheck: Record<number, boolean> = {};
  keyItems: any[] = [];
  originalKey: any = null;
  selectedKey: any = null;
  loading = true;
  saving = false;
  readonly passwordPattern = '^[!-~]+$';

  get userPermission(): number { return this.authState.currentUser?.role ?? 99; }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('店舗管理', 's-icon-studio');
    const userId = +this.route.snapshot.params['userId'];
    try {
      const [userData, userStudios, allStudios] = await Promise.all([
        this.userService.list(String(userId)) as Promise<any>,
        this.userService.getUsersStudios(userId) as Promise<any[]>,
        this.studioService.list('all') as Promise<any[]>,
      ]);

      const raw = userData;
      this.form = Array.isArray(raw) ? (raw[0] ?? { id: userId }) : (raw ?? { id: userId });
      if (!this.form.id) this.form.id = userId;

      this.ownerName = this.form.owner?.name ?? '';
      const ownerId = this.form.owner?.id;

      this.filteredStudios = ownerId
        ? (allStudios ?? []).filter((s: any) => s.ownerId === ownerId)
        : (allStudios ?? []);

      // Pre-check assigned studios
      const assignedStudioIds = new Set((userStudios ?? []).map((s: any) => s.studioId));
      for (const studio of this.filteredStudios) {
        this.studioCheck[studio.id] = assignedStudioIds.has(studio.id);
      }

      // Load key numbers for owner's studios
      const studioIds = this.filteredStudios.map((s: any) => s.id);
      if (studioIds.length > 0) {
        const keyResults = await Promise.all(
          studioIds.map((id: number) => this.studioService.getKeyNumber(id) as Promise<any[]>)
        );
        const allKeys = keyResults.flat().filter(Boolean);
        this.keyItems = allKeys.filter((v: any) => {
          const isCurrentUser = v.user?.id === this.form.id;
          const available = v.user === null || isCurrentUser || v.id !== v.user?.keyNumberId;
          return available && v.isStaff;
        });
        const currentKeyId = this.form.keyNumber?.id ?? this.form.keyNumberId;
        this.originalKey = allKeys.find((v: any) =>
          v.id === currentKeyId || v.user?.id === this.form.id
        ) ?? this.form.keyNumber ?? null;
        this.selectedKey = this.originalKey
          ? (this.keyItems.find((v: any) => v.id === this.originalKey.id) ?? this.originalKey)
          : null;
      }
    } finally {
      this.loading = false;
    }
  }

  getCheckedStudioIds(): number[] {
    return Object.entries(this.studioCheck)
      .filter(([, checked]) => checked)
      .map(([id]) => Number(id));
  }

  // 旧版 staff.js:370-375 の isSelectedStudioKey() と同じく、チェック済み店舗のキーだけに絞る
  get filteredKeyItems(): any[] {
    const checkedIds = this.getCheckedStudioIds();
    return this.keyItems.filter((v: any) => checkedIds.includes(v.studioId));
  }

  hasCheckedStudio(): boolean {
    return this.getCheckedStudioIds().length > 0;
  }

  async save(form?: NgForm): Promise<void> {
    if (form?.invalid || !this.form?.name || !this.form?.email || !this.hasCheckedStudio()) {
      form?.control.markAllAsTouched();
      return;
    }
    this.saving = true;
    try {
      const payload = { ...this.form };
      delete payload['owner'];
      delete payload['studio'];
      delete payload['createdAt'];
      delete payload['updatedAt'];
      delete payload['status'];
      await this.userService.upsert(payload);
      const userId = this.form.id;
      await this.userService.clearUsersStudios(userId);
      const studioEntries = this.getCheckedStudioIds().map(studioId => ({ userId, studioId }));
      if (studioEntries.length > 0) {
        await this.userService.createUsersStudios(studioEntries);
      }
      // 旧版 staff.js:319-337: 成功 Flash、キー更新失敗は danger Flash（表示は遷移先 staff 一覧の <flash-message>）
      this.flash.create('success', '従業員データを更新しました');
      const currentKey = this.originalKey;
      if (!this.selectedKey && currentKey !== null) {
        await this.keyNumberService.release({ id: currentKey.id, userId });
        await this.userService.upsert({ id: userId, keyNumberId: null });
      } else if (this.selectedKey && (!currentKey || this.selectedKey.id !== currentKey.id)) {
        const keyResult = await this.keyNumberService.update({ ...this.selectedKey, userId });
        if (!keyResult || keyResult === 'No target') {
          this.flash.create('danger', '入退館キーの更新に失敗しました。従業員編集から改めて設定してください');
        }
      }
      this.router.navigate(['/staff']);
    } finally {
      this.saving = false;
    }
  }
}
